"use client"

import { useState } from 'react'
import { actorColor, actorInitials } from './avatar-identity'

/**
 * Presentational avatar for ANY person, keyed on explicit props (not the
 * auth session). One place for the img → onError → identity-coloured
 * initials fallback used across the profile page, organisation member
 * list, and version history. `UserAvatar`/`UserClientAvatar` render the
 * SIGNED-IN user and delegate here; pass a specific person's fields to
 * show anyone else.
 *
 * `identityKey` is the stable identity string (identifier or email) — it
 * drives both the deterministic fallback colour and the initials, so one
 * person keeps one colour everywhere.
 */
export function PersonAvatar({
  imageUrl,
  identityKey,
  name,
  sizePx = 32,
  className = '',
}: {
  imageUrl?: string | null
  identityKey: string
  name?: string
  sizePx?: number
  className?: string
}) {
  const [broken, setBroken] = useState(false)
  const dims = { width: sizePx, height: sizePx }

  if (imageUrl && !broken) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt={name ?? identityKey}
        // lh3.googleusercontent rate-limits bursts and 403s on referrer —
        // no-referrer + onError fall back to initials instead of a broken glyph
        referrerPolicy="no-referrer"
        className={`rounded-full object-cover ${className}`}
        style={dims}
        onError={() => setBroken(true)}
      />
    )
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold text-white ${className}`}
      style={{
        ...dims,
        backgroundColor: actorColor(identityKey),
        fontSize: Math.round(sizePx * 0.4),
      }}
      title={name ?? identityKey}
    >
      {actorInitials(identityKey)}
    </span>
  )
}
